import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { LogIn, AlertCircle, FileText, Clock, Shield, BarChart3, Eye, EyeOff } from 'lucide-react';

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string; delay: string }> = ({ 
  icon, title, description, delay 
}) => (
  <div 
    className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 xl:p-5 border border-white/20 
               hover:bg-white/20 hover:scale-105 hover:border-white/40
               transition-all duration-300 cursor-default group
               animate-fade-in-up"
    style={{ animationDelay: delay }}
  >
    <div className="text-white/90 mb-2 xl:mb-3 group-hover:scale-110 transition-transform duration-300">
      {icon}
    </div>
    <h3 className="text-white font-semibold text-sm sm:text-base xl:text-lg mb-1 xl:mb-2">{title}</h3>
    <p className="text-blue-100 text-xs sm:text-sm leading-relaxed">{description}</p>
  </div>
);

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: <FileText size={32} />,
      title: 'Gestión de Facturas',
      description: 'Administra todas tus facturas en un solo lugar con seguimiento de estados y pendientes.',
      delay: '0.1s'
    },
    {
      icon: <Clock size={32} />,
      title: 'Tiempo Real',
      description: 'Comentarios y actualizaciones instantáneas con notificaciones en vivo.',
      delay: '0.2s'
    },
    {
      icon: <Shield size={32} />,
      title: 'Acceso Seguro',
      description: 'Sistema de roles diferenciado para administradores y clientes con autenticación JWT.',
      delay: '0.3s'
    },
    {
      icon: <BarChart3 size={32} />,
      title: 'Reportes y Análisis',
      description: 'Genera reportes detallados y analiza PDFs de facturación automáticamente.',
      delay: '0.4s'
    }
  ];

  return (
    <div className="min-h-screen flex">
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.6;
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          opacity: 0;
        }
        .animate-slide-in-left {
          animation: slide-in-left 0.5s ease-out forwards;
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>

      {/* Left Side - Blue - Features */}
      <div className="hidden md:flex w-1/2 xl:w-3/5 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 
                      items-center justify-center p-6 lg:p-8 xl:p-12 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-60 lg:w-80 h-60 lg:h-80 bg-white/10 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute -bottom-40 -left-40 w-72 lg:w-96 h-72 lg:h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] lg:w-[600px] h-[400px] lg:h-[600px] bg-indigo-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative z-10 w-full max-w-xs lg:max-w-md xl:max-w-lg animate-slide-in-left">
          <h2 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-2 xl:mb-3 animate-fade-in-up">
            Bienvenido a iBilling Solutions
          </h2>
          <p className="text-blue-100 mb-4 lg:mb-6 xl:mb-8 text-sm lg:text-base xl:text-lg animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
            La plataforma integral para la gestión de facturación médica
          </p>

          <div className="grid grid-cols-2 gap-2 lg:gap-3 xl:gap-4">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - White - Login Form */}
      <div className="w-full md:w-1/2 xl:w-2/5 bg-white flex items-center justify-center p-4 sm:p-6 lg:p-8 min-h-screen md:min-h-0">
        <div className="w-full max-w-sm lg:max-w-md animate-fade-in-up">
          <div className="text-center mb-6 lg:mb-8">
            <img src="/logo.svg" alt="iBilling" className="h-16 lg:h-20 max-w-[240px] lg:max-w-[280px] mx-auto mb-3 lg:mb-4" />
            <p className="text-gray-500 text-sm lg:text-base">Portal de Facturación</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 lg:px-4 py-2 lg:py-3 rounded-lg flex items-center gap-2 animate-fade-in-up text-sm">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                Correo Electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 lg:px-4 py-2.5 lg:py-3 text-sm lg:text-base border border-gray-300 rounded-lg 
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                         hover:border-blue-400
                         transition-all duration-200"
                placeholder="correo@ejemplo.com"
                required
              />
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1 lg:mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 lg:px-4 py-2.5 lg:py-3 pr-10 text-sm lg:text-base border border-gray-300 rounded-lg 
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                           hover:border-blue-400
                           transition-all duration-200"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 hover:shadow-lg hover:scale-[1.02]
                         text-white font-semibold py-2.5 lg:py-3 px-4 rounded-lg text-sm lg:text-base
                         transition-all duration-200 
                         flex items-center justify-center gap-2 
                         disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-none
                         active:scale-[0.98]"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 lg:w-5 h-4 lg:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Iniciando sesión...</span>
                  </>
                ) : (
                  <>
                    <LogIn size={18} className="lg:w-5 lg:h-5" />
                    Iniciar Sesión
                  </>
                )}
              </button>
            </div>
          </form>

          <p className="text-center text-gray-400 text-xs lg:text-sm mt-6 lg:mt-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            © 2024 iBilling Solutions. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
